/**
 * AI 소개영상용 Gemini 호출기 — Supabase Edge Function(erp-video) 중계 방식
 *
 * geminiClient.js 와 같은 이유(키를 브라우저에 두지 않는다)로 우리 Edge Function 만 부른다.
 * 채팅(erp-chat)과 함수를 따로 둔 이유는 erp-video/index.ts 머리말 참고.
 *
 * 하는 일:
 *   requestIntroScript  — 일정 데이터 + 상세정보 콘텐츠 글 → 30초 대본(JSON)
 *   requestNarration    — 나레이션 문장 → 음성(PCM 16bit mono, base64)
 *   generateClip        — 장면 사진(첫 프레임) + 카메라 지시 → Veo 영상 클립(mp4 Blob). 유료, 고급 옵션
 *   requestDetailCopy   — (AI 상세 이미지) 같은 상품 데이터 → 상세페이지 섹션별 문구(JSON)
 *   generateImage       — (AI 상세 이미지) 사진이 없는 섹션용 AI 풍경 한 장(base64). 장당 과금
 *   requestMockupPlan   — (목업 → AI 동영상) 목업 mp4 프레임 → 장면별 Veo 지시
 *   generateOmni        — (목업 → AI 동영상) 10초 이하 목업 mp4 를 Gemini Omni Flash 에 직접 넣어 실사 영상으로. 유료
 */

import { isAiConfigured, GeminiError } from './geminiClient';

const FUNCTION_NAME = 'erp-video';

// ── 토큰 사용 기록 ───────────────────────────────────────────────
// erp-video 응답에 실려 오는 usageMetadata 를 모아 둔다. 모달이 "이 영상에 쓴 토큰·비용" 을 보여 주는 데 쓴다.
// 정확한 청구액이 아니라 공개 단가(아래 PRICES)로 계산한 추정치다.

/**
 * [{ action:'script'|'detail_copy'|'tts'|'image', model, usage:{ promptTokenCount, candidatesTokenCount, thoughtsTokenCount, totalTokenCount }, at }]
 * Veo 는 토큰이 아니라 초 단위 과금이라 { action:'veo', model, tier, seconds, at } 로 적는다.
 */
export const usageLog = [];
export function resetUsageLog() { usageLog.length = 0; }

/** USD / 100만 토큰. 2026-09 기준 ai.google.dev/gemini-api/docs/pricing (표준 요금, 20만 토큰 이하) */
const PRICES = [
  { match: /3\.1-flash-tts/, input: 1.0, output: 20 },
  { match: /tts/, input: 0.5, output: 10 },
  // 이미지 모델(gemini-2.5-flash-image, gemini-3-pro-image 등)은 이름에 flash/pro 가 들어가므로 그보다 먼저. 출력은 이미지 토큰(장당 1290) 단가
  { match: /image/, input: 0.30, output: 30 },
  { match: /pro/, input: 2.0, output: 12 },
  { match: /flash/, input: 0.75, output: 3.75 },
];
const priceOf = (model) => PRICES.find((p) => p.match.test(String(model || '')));

/**
 * Veo 등급. 서버(erp-video)의 VEO_TIERS 와 같은 이름. usdPerSec 는 720p 기준 공개 단가(2026-09) — 견적 표시용.
 * lite 는 이미지→영상만 되지만 사진 기반이라 그걸로 충분한 경우가 많다.
 */
export const VEO_TIERS = [
  { id: 'lite', label: 'Lite (가장 저렴)', usdPerSec: 0.05 },
  { id: 'fast', label: 'Fast (권장)', usdPerSec: 0.10 },
  { id: 'standard', label: 'Standard (최고 품질)', usdPerSec: 0.40 },
];
export const veoUsdPerSec = (tier) => VEO_TIERS.find((t) => t.id === tier)?.usdPerSec ?? VEO_TIERS[1].usdPerSec;

/**
 * 사용 기록을 합산한다. 출력에는 생각(thoughts) 토큰도 들어간다 — Pro 모델은 이게 답보다 클 때가 많다.
 * @returns {{ script:{calls,input,output}, tts:{calls,input,output}, image:{calls,input,output}, other:{calls,input,output}, veo:{calls,seconds,usd}, usd:number|null }}
 *   usd 는 단가를 모르는 모델이 하나라도 있으면 null. veo.usd 는 Veo 몫만 따로(전체 usd 에도 포함돼 있다).
 *   script 에는 상세페이지 카피(detail_copy)도 들어간다 — 같은 종류의 텍스트 호출이다. image 는 AI 정지 이미지(장당 출력 ~1290토큰).
 */
export function summarizeUsage(entries = usageLog) {
  const out = { script: { calls: 0, input: 0, output: 0 }, tts: { calls: 0, input: 0, output: 0 }, image: { calls: 0, input: 0, output: 0 }, other: { calls: 0, input: 0, output: 0 }, veo: { calls: 0, seconds: 0, usd: 0 }, usd: 0 };
  let unknown = false;
  for (const e of entries) {
    if (e.action === 'veo' || e.action === 'omni') {
      const cost = (Number(e.seconds) || 0) * (e.action === 'omni' ? OMNI_USD_PER_SEC_ESTIMATE : veoUsdPerSec(e.tier));
      out.veo.calls++; out.veo.seconds += Number(e.seconds) || 0; out.veo.usd += cost; out.usd += cost;
      continue;
    }
    const u = e.usage || {};
    const input = Number(u.promptTokenCount) || 0;
    const output = (Number(u.candidatesTokenCount) || 0) + (Number(u.thoughtsTokenCount) || 0);
    const bucket = e.action === 'script' || e.action === 'detail_copy' ? out.script : e.action === 'tts' ? out.tts : e.action === 'image' || e.action === 'image_edit' ? out.image : out.other;
    bucket.calls++; bucket.input += input; bucket.output += output;
    const price = priceOf(e.model);
    if (price) out.usd += (input * price.input + output * price.output) / 1e6;
    else unknown = true;
  }
  if (unknown) out.usd = null;
  return out;
}

const getSupabaseUrl = () => (process.env.REACT_APP_SUPABASE_URL || '').trim().replace(/\/$/, '');
const getAnonKey = () => (process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
const getAppToken = () => (process.env.REACT_APP_ERP_APP_TOKEN || '').trim();

async function callVideoFunction(body, signal) {
  if (!isAiConfigured()) {
    throw new GeminiError(
      'AI 연결 설정이 없습니다. .env 에 REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY / REACT_APP_ERP_APP_TOKEN 를 넣고 개발 서버를 다시 시작하세요.',
    );
  }
  let response;
  try {
    response = await fetch(`${getSupabaseUrl()}/functions/v1/${FUNCTION_NAME}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: getAnonKey(),
        Authorization: `Bearer ${getAnonKey()}`,
        'x-erp-app-token': getAppToken(),
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new GeminiError(`AI 서버에 연결하지 못했습니다: ${error?.message || error}`);
  }

  let data = null;
  try {
    data = await response.json();
  } catch {
    // 아래에서 상태코드로 처리
  }
  if (!response.ok || !data?.ok) {
    const message = data?.error || `HTTP ${response.status}`;
    // 404 는 함수가 아직 배포되지 않았을 때 가장 흔하다. 원인을 바로 알 수 있게 덧붙인다.
    // 404: 함수 미배포. 546: Supabase Edge Function 이 정상 응답 없이 죽음 — 실행 시간·CPU 한도 초과(WORKER_LIMIT)나 부팅 오류. 대시보드 Logs 에 원인이 남는다
    const hint = response.status === 404
      ? ' (erp-video 함수가 배포되지 않았을 수 있습니다: supabase/functions/README.md)'
      : response.status === 546
        ? ' (Supabase Edge Function 이 한도 초과나 오류로 중단됐습니다. 대본이면 길이를 줄이거나 사진 수를 줄여 다시 시도하고, 반복되면 Supabase 대시보드 → Edge Functions → erp-video → Logs 를 확인하세요)'
        : '';
    throw new GeminiError(`${message}${hint}`, response.status);
  }
  if (data.usage) usageLog.push({ action: body.action, model: data.model, usage: data.usage, at: Date.now() });
  return data;
}

/**
 * 일정 데이터와 상세정보 글로 대본을 받는다.
 * @param {object} p
 * @param {object} p.product  { name, cityName, keywords, days:[{ day, places:[{name, desc, flight}], meals, hotels:[string] }], extras:[{title,text}] }
 *                            extras 는 상세정보 콘텐츠(상품특징·포함/불포함·특전·호텔·식사 등)를 텍스트로 뽑은 것 — AiIntroVideoModal.extractContentExtras
 * @param {number} [p.targetSec=30]  15~90 (서버가 이 범위로 자른다)
 * @param {'calm'|'energetic'|'premium'} [p.tone='calm']
 * @param {'promo'|'story'|'show'} [p.format='promo']  promo: 소개형 / story: 체험형(1인칭) / show: 예능형(주인공 3인칭)
 * @param {{ gender:'female'|'male', age:'20s'|'30s'|'40s'|'50s'|'60s', companion:'solo'|'couple'|'friends'|'family' }} [p.hero]  예능형 주인공 (나레이션은 소개형 그대로, 영상만 3인칭)
 * @param {Array<{id:number, source:string, data:string}>} [p.photos]  AI 에게 보여줄 썸네일(base64 jpeg). 주면 장면마다 photoIds 를 골라준다
 * @param {AbortSignal} [p.signal]
 * @returns {Promise<{ keyPoints:string[], title, subtitle, closing, scenes:Array<{label, dayNumbers:number[], title, caption, narration, photoIds:number[]}> }>}
 *          keyPoints 는 AI 가 extras(상세정보)에서 뽑은 매력 포인트. 서버가 이 포인트가 나레이션에 들어갔는지 확인하고 안 들어갔으면 한 번 다시 쓰게 한다.
 */
export async function requestIntroScript({ product, targetSec = 30, tone = 'calm', format = 'promo', hero, photos, signal }) {
  const data = await callVideoFunction({ action: 'script', product, targetSec, tone, format, ...(hero ? { hero } : {}), ...(photos?.length ? { photos } : {}) }, signal);
  return data.script;
}

/** 대본 형식. 서버(erp-video)의 format 과 같은 이름. */
export const SCRIPT_FORMATS = [
  { id: 'promo', label: '소개형 (일정·특징 안내)', hint: '인트로 → 경로 → 특징 → 일정 → 마무리. 상품을 설명하는 영상. 사진만으로도 좋습니다.' },
  { id: 'story', label: '체험형 (1인칭, 영화처럼)', hint: '보는 사람이 그 여행을 하는 것처럼 1인칭 현재형 순간들로 씁니다. 특징 설명 없음. AI 영상(Veo)과 함께 쓰는 것을 전제로 합니다.' },
  { id: 'show', label: '예능형 (주인공 3인칭)', hint: '나레이션은 소개형과 같은 일정 소개이고, 영상만 주인공 한 명(성별·연령대·동행 선택)이 그 일정을 여행하는 모습을 3인칭으로 보여 줍니다. 주인공 외형을 모든 클립에 똑같이 붙여 같은 사람으로 보이게 합니다. AI 영상 전제.' },
];
export const HERO_OPTIONS = {
  gender: [{ id: 'female', label: '여성' }, { id: 'male', label: '남성' }],
  age: [{ id: '20s', label: '20대' }, { id: '30s', label: '30대' }, { id: '40s', label: '40대' }, { id: '50s', label: '50대' }, { id: '60s', label: '60대' }],
  companion: [{ id: 'solo', label: '혼자' }, { id: 'couple', label: '연인/배우자와' }, { id: 'friends', label: '친구와' }, { id: 'family', label: '가족과' }],
};

/** 톤 선택지. 서버(erp-video)의 TONES 와 같은 이름이어야 한다. */
export const SCRIPT_TONES = [
  { id: 'calm', label: '차분한 소개' },
  { id: 'energetic', label: '활기찬 홍보' },
  { id: 'premium', label: '프리미엄' },
];

// ── AI 상세 이미지 (상세페이지 카피 + 정지 이미지) ──────────────────

/**
 * 상세페이지 카피 — 일정 데이터 + 상세정보 글 (+사진 썸네일) → 섹션별 문구 JSON.
 * @param {object} p
 * @param {object} p.product  requestIntroScript 와 같은 모양
 * @param {'calm'|'energetic'|'premium'} [p.tone='calm']
 * @param {Array<{id:number, source:string, data:string}>} [p.photos]  주면 heroPhotoIds 와 섹션별 photoIds 를 골라준다
 * @param {AbortSignal} [p.signal]
 * @returns {Promise<{ headline:string, subheadline:string, badge:string, intro:string, heroPhotoIds:number[],
 *   highlights:Array<{ title:string, desc:string, photoIds:number[], imagePrompt:string }>,
 *   days:Array<{ day:number, title:string, summary:string, photoIds:number[] }>,
 *   includes:string[], excludes:string[], notes:string[], closing:string, heroImagePrompt:string }>}
 *   days 는 상품의 모든 일차가 순서대로(모델이 빠뜨린 일차는 title·summary 가 빈 문자열). imagePrompt 류는 영어 — generateImage 의 description 으로 넘긴다.
 */
export async function requestDetailCopy({ product, tone = 'calm', photos, signal }) {
  const data = await callVideoFunction({ action: 'detail_copy', product, tone, ...(photos?.length ? { photos } : {}) }, signal);
  return data.copy;
}

/**
 * 섹션용 AI 정지 이미지 한 장. 사진이 없는 섹션에만 쓴다 (장당 과금, IMAGE_USD_PER_IMAGE 참고).
 * @param {object} p
 * @param {string} p.description  그릴 풍경(영어, 800자 이내). 서버가 사진풍·글자/로고/얼굴 금지를 붙인다
 * @param {string} [p.prompt]     사용자가 고친 최종 프롬프트(1200자 이내). 있으면 서버 조립 대신 그대로 쓴다
 * @param {'1:1'|'4:3'|'3:4'|'16:9'|'9:16'} [p.aspectRatio='4:3']
 * @param {AbortSignal} [p.signal]
 * @returns {Promise<{ data:string, mimeType:string, prompt:string }>}  data 는 base64(data: 접두사 없음), prompt 는 실제로 쓰인 최종 프롬프트
 */
export async function generateImage({ description, prompt, aspectRatio = '4:3', signal }) {
  const data = await callVideoFunction({ action: 'image', description, aspectRatio, ...(prompt ? { prompt } : {}) }, signal);
  return { ...data.image, prompt: data.prompt };
}

/**
 * (목업 → AI 동영상) 장면 시작 화면 고치기 — 구도·앵글은 그대로, instruction 대로(예: 배경을 사그라다 파밀리아로). 장당 과금.
 * @param {object} p
 * @param {{ data:string, mimeType:string }} p.image  고칠 화면(base64, data: 접두사 없음)
 * @param {string} [p.instruction]  수정 요청(500자 이내, 한국어 가능)
 * @param {Array<{ data:string, mimeType:string }>} [p.references]  참고 사진(≤3장) — 그 장소·사물이 실제로 이렇게 생겼다
 * @param {string} [p.description]  그 장면의 Veo 설명(맥락용)
 * @param {'16:9'|'9:16'} [p.aspectRatio='16:9']
 * @param {'edit'|'compose'} [p.role='edit']  compose: image 는 구도 안내일 뿐, references 의 내용을 그 구도로 새로 그린다
 * @returns {Promise<{ data:string, mimeType:string }>}
 */
export async function editImage({ image, instruction, references, description, aspectRatio = '16:9', role = 'edit', signal }) {
  const data = await callVideoFunction({ action: 'image_edit', image, aspectRatio, role, ...(instruction ? { instruction } : {}), ...(references?.length ? { references } : {}), ...(description ? { description } : {}) }, signal);
  return data.image;
}

/** 이미지 1장 예상 단가 (USD). gemini-2.5-flash-image 출력 1290토큰 × $30/M ≈ $0.039 (2026-09 공개 단가) */
export const IMAGE_USD_PER_IMAGE = 0.039;

/**
 * 나레이션 문장을 음성으로 바꾼다.
 * @returns {Promise<{ data: string, mimeType: string, sampleRate: number }>}  data 는 base64 PCM(16bit, mono)
 */
export async function requestNarration({ text, voice, tone = 'calm', signal }) {
  const data = await callVideoFunction({ action: 'tts', text, voice, tone }, signal);
  return data.audio;
}

/** 목소리 선택지. 서버(erp-video)의 VOICES 와 같은 이름이어야 한다. */
export const NARRATION_VOICES = [
  { id: 'Kore', label: '코레 (여성·차분)' },
  { id: 'Aoede', label: '아오이데 (여성·밝음)' },
  { id: 'Leda', label: '레다 (여성·젊음)' },
  { id: 'Zephyr', label: '제피르 (여성·경쾌)' },
  { id: 'Puck', label: '퍽 (남성·활기)' },
  { id: 'Charon', label: '카론 (남성·중저음)' },
  { id: 'Fenrir', label: '펜리르 (남성·힘있음)' },
  { id: 'Orus', label: '오루스 (남성·단정)' },
];

// ── AI 영상 클립 (Veo) ───────────────────────────────────────────
// ⚠ 유료 (초당 과금, VEO_TIERS 참고). 시작 → 폴링 → 내려받기 세 단계이고 클립 하나에 보통 1~3분 걸린다.

/** 서버가 조립할 최종 프롬프트 미리보기 (Gemini 호출 없음). promptKo 는 한국어 대역(읽기용). @returns {Promise<{ prompt:string, promptKo:string }>} */
export async function previewClipPrompt({ description, descriptionKo, mode = 'photo', hasImage = false, hasReference = false, character, signal }) {
  const data = await callVideoFunction({ action: 'veo_prompt', description, descriptionKo, mode, hasImage, hasReference, ...(character ? { character } : {}) }, signal);
  return { prompt: String(data.prompt || ''), promptKo: String(data.promptKo || '') };
}

/**
 * 생성을 시작한다.
 * @param {object} p
 * @param {string} p.description                   화면에 보여야 할 것 (300자 이내). 서버가 방식(mode)별 뼈대와 글자·로고·얼굴 금지를 붙인다
 * @param {string} [p.prompt]                       사용자가 확인 창에서 고친 최종 프롬프트(1500자 이내). 있으면 서버 조립 대신 그대로 쓴다
 * @param {'photo'|'action'|'story'} [p.mode='photo']  photo: 사진 그대로 움직임 / action: 사진 속 장소에서 사람의 행동 / story: 사진 없이 새 장면(이동·체험)
 * @param {{ data:string, mimeType:string }} [p.image]  첫 프레임 사진(base64, data: 접두사 없음). photo/action 에서 쓴다. story 는 서버가 무시한다
 * @param {{ data:string, mimeType:string }} [p.reference]  story 에서 "비슷한 장소·분위기" 참고 사진. lite 는 서버가 무시. 쓰면 8초 고정
 * @param {string} [p.character]                    예능형 주인공 캐릭터 시트(영어). 모든 클립 프롬프트에 붙는다
 * @param {{ data:string, mimeType:string }} [p.heroImage]  주인공 사진(선택). story·lite 아닐 때 참고 이미지로 같이 간다
 * @param {'16:9'|'9:16'} [p.aspectRatio='16:9']
 * @param {4|6|8} [p.durationSeconds=8]
 * @param {'lite'|'fast'|'standard'} [p.tier='fast']
 * @returns {Promise<{ operation:string, model:string, tier:string, durationSeconds:number }>}
 */
export async function startClip({ description, prompt, mode = 'photo', image, reference, character, heroImage, aspectRatio = '16:9', durationSeconds = 8, tier = 'fast', signal }) {
  const data = await callVideoFunction({ action: 'veo_start', description, mode, aspectRatio, durationSeconds, tier, ...(prompt ? { prompt } : {}), ...(image ? { image } : {}), ...(reference ? { reference } : {}), ...(character ? { character } : {}), ...(heroImage ? { heroImage } : {}) }, signal);
  return { operation: data.operation, model: data.model, tier: data.tier || tier, durationSeconds: Number(data.durationSeconds) || durationSeconds };
}

/** 작업 상태를 본다. @returns {Promise<{ done: boolean, uri?: string }>} */
export async function pollClip({ operation, signal }) {
  const data = await callVideoFunction({ action: 'veo_status', operation }, signal);
  return { done: !!data.done, uri: data.uri };
}

/** 완성된 클립을 서버를 거쳐 받는다 (결과 주소가 API 키로 인증되기 때문). @returns {Promise<Blob>} mp4 */
export async function fetchClip({ uri, signal }) {
  return fetchVideoBytes({ action: 'veo_fetch', uri }, signal);
}

/** erp-video 의 *_fetch 액션은 JSON 이 아니라 mp4 바이트를 그대로 흘려보낸다 */
async function fetchVideoBytes(body, signal) {
  let response;
  try {
    response = await fetch(`${getSupabaseUrl()}/functions/v1/${FUNCTION_NAME}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: getAnonKey(),
        Authorization: `Bearer ${getAnonKey()}`,
        'x-erp-app-token': getAppToken(),
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new GeminiError(`AI 서버에 연결하지 못했습니다: ${error?.message || error}`);
  }
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try { message = (await response.json())?.error || message; } catch { /* 본문이 JSON 이 아님 */ }
    throw new GeminiError(message, response.status);
  }
  const blob = await response.blob();
  if (!blob.size) throw new GeminiError('받은 영상이 비어 있습니다.');
  return blob;
}

/**
 * 목업 영상 분석 — 목업 mp4 에서 뽑은 프레임 → 장면 계획 (Veo 는 mp4 를 입력으로 못 받는다. erp-video 의 mockup_plan 참고).
 * @param {object} p
 * @param {Array<{ t:number, data:string }>} p.frames  시간 순 프레임(base64 jpeg, 24장 이내)
 * @param {number} p.durationSec  목업 길이(64초 이내)
 * @param {'16:9'|'9:16'} [p.aspectRatio='16:9']
 * @param {number} [p.maxScenes=6]  최대 8
 * @param {string} [p.notes]  사용자 요청사항(500자 이내)
 * @param {Array<{ data:string, mimeType:string }>} [p.references]  렌더링 사진(≤3). 주면 목업은 카메라·구도 안내로만, 사진은 그릴 내용으로 분석한다(refMode)
 * @returns {Promise<{ summary:string, refMode:boolean, scenes:Array<{ start:number, end:number, mode:'photo'|'action'|'story', firstFrame:boolean, overlayText:string, description:string, descriptionKo:string, refs:number[] }> }>}
 *   refs: 그 장면이 그릴 사진 번호(0부터, references 순서)
 */
export async function requestMockupPlan({ frames, durationSec, aspectRatio = '16:9', maxScenes = 6, notes, references, signal }) {
  const data = await callVideoFunction({ action: 'mockup_plan', frames, durationSec, aspectRatio, maxScenes, ...(notes ? { notes } : {}), ...(references?.length ? { references } : {}) }, signal);
  return data.plan;
}

// ── Gemini Omni Flash (목업 영상 → 실사 영상) ─────────────────────
// ⚠ 유료. 요금은 2026-10 기준 공식 문서에서 확인하지 못해 외부 공개가(초당 약 $0.11)로 추정한다 — 실제 청구서로 맞출 것.
export const OMNI_USD_PER_SEC_ESTIMATE = 0.11;
/** Omni 가 편집 입력으로 받는 영상 길이 상한(초). 서버·Google 문서와 같은 값 */
export const OMNI_MAX_INPUT_SEC = 10;

/**
 * 목업 영상(+ 렌더링 사진)으로 실사 영상을 만든다. 시작 → 결과 파일이 ACTIVE 가 될 때까지 폴링 → 내려받기.
 * @param {object} p
 * @param {{ data:string }} p.video  목업 mp4 base64(10초 이하, 3MB 이하 — 브라우저에서 다시 인코딩해 줄인 것)
 * @param {Array<{ data:string, mimeType:string }>} [p.references]  렌더링 사진(≤3)
 * @param {string} [p.instruction]  추가 요청(500자 이내, 한국어 가능)
 * @param {'16:9'|'9:16'} [p.aspectRatio='16:9']
 * @param {'720p'|'1080p'} [p.resolution='720p']
 * @param {(s:{ elapsedSec:number, state:string })=>void} [p.onTick]
 * @returns {Promise<{ blob:Blob, model:string }>}
 */
export async function generateOmni({ video, references, instruction, aspectRatio = '16:9', resolution = '720p', seconds = 0, signal, onTick, maxWaitMs = 8 * 60 * 1000 }) {
  let started = null;
  for (let attempt = 0; ; attempt++) {
    try {
      started = await callVideoFunction({ action: 'omni_start', video, aspectRatio, resolution, ...(references?.length ? { references } : {}), ...(instruction ? { instruction } : {}) }, signal);
      break;
    } catch (e) {
      if (e?.name === 'AbortError' || e?.status !== 429 || attempt >= 3) throw e;
      await sleep(20000 * (attempt + 1), signal);
    }
  }
  // 시작되면 Google 쪽에서 생성이 돌아가므로(중단해도) 바로 비용 기록에 넣는다
  usageLog.push({ action: 'omni', model: started.model, seconds, at: Date.now() });
  const startedAt = Date.now();
  // 통신 오류(5xx·연결)만 다시 시도한다. 4xx 는 다시 해도 같다
  const withRetry = async (fn) => {
    for (let attempt = 0; ; attempt++) {
      try { return await fn(); } catch (e) {
        if (e?.name === 'AbortError' || attempt >= 2 || (e?.status >= 400 && e?.status < 500)) throw e;
        await sleep(3000 * (attempt + 1), signal);
      }
    }
  };
  for (;;) {
    await sleep(5000, signal);
    const st = await withRetry(() => callVideoFunction({ action: 'omni_status', fileId: started.fileId }, signal));
    onTick?.({ elapsedSec: Math.round((Date.now() - startedAt) / 1000), state: st.state || '' });
    if (st.failed) throw new GeminiError(st.error || 'AI 영상 변환에 실패했습니다.');
    if (st.done) {
      const blob = await withRetry(() => fetchVideoBytes({ action: 'omni_fetch', fileId: started.fileId }, signal));
      return { blob, model: started.model };
    }
    if (Date.now() - startedAt > maxWaitMs) throw new GeminiError('AI 영상 변환이 너무 오래 걸립니다. 잠시 후 다시 시도해 주세요.');
  }
}

/** 중단 신호가 오면 바로 깨는 sleep */
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new DOMException('중단됨', 'AbortError')); return; }
    const onAbort = () => { clearTimeout(t); reject(new DOMException('중단됨', 'AbortError')); };
    const t = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * 시작부터 내려받기까지 한 번에. 폴링 간격 5초, 최대 maxWaitMs.
 * 성공했을 때만 usageLog 에 초 단위로 적는다 (Google 도 성공한 영상만 과금한다).
 * @param {(status:{ elapsedSec:number })=>void} [onTick]
 * @returns {Promise<{ blob:Blob, durationSeconds:number, model:string, tier:string }>}
 */
export async function generateClip({ description, prompt, mode, image, reference, character, heroImage, aspectRatio, durationSeconds, tier, signal, onTick, maxWaitMs = 6 * 60 * 1000 }) {
  // Veo 는 분당 요청 한도가 아주 낮다. 429 면 20·40·60초 쉬었다가 다시 시작한다 (시작 전이라 과금 없음).
  let started = null;
  for (let attempt = 0; ; attempt++) {
    try { started = await startClip({ description, prompt, mode, image, reference, character, heroImage, aspectRatio, durationSeconds, tier, signal }); break; } catch (e) {
      if (e?.name === 'AbortError' || e?.status !== 429 || attempt >= 3) throw e;
      await sleep(20000 * (attempt + 1), signal);
    }
  }
  const startedAt = Date.now();
  // 폴링·내려받기는 몇 번 실패해도 괜찮다 — 작업은 Google 쪽에서 계속 돌고 있고(그리고 과금되므로) 잠깐의 통신 오류로 결과를 버리면 안 된다.
  // 안전필터(502)는 세 번 다 실패하고 끝나니 무해하다.
  const withRetry = async (fn) => {
    for (let attempt = 0; ; attempt++) {
      try { return await fn(); } catch (e) {
        if (e?.name === 'AbortError' || attempt >= 2) throw e;
        await sleep(3000 * (attempt + 1), signal);
      }
    }
  };
  for (;;) {
    await sleep(5000, signal);
    onTick?.({ elapsedSec: Math.round((Date.now() - startedAt) / 1000) });
    const { done, uri } = await withRetry(() => pollClip({ operation: started.operation, signal }));
    if (done && uri) {
      const blob = await withRetry(() => fetchClip({ uri, signal }));
      usageLog.push({ action: 'veo', model: started.model, tier: started.tier, seconds: started.durationSeconds, at: Date.now() });
      return { blob, durationSeconds: started.durationSeconds, model: started.model, tier: started.tier };
    }
    if (Date.now() - startedAt > maxWaitMs) throw new GeminiError('AI 영상 생성이 너무 오래 걸립니다. 잠시 후 다시 시도해 주세요.');
  }
}
